import React, { useCallback, useMemo, useState } from 'react'
import DynamicList from '../shared/dynamic_list'

const FIELD_PREFIX = 'safari_setting[website_permissions_attributes]'

type PermissionOption = 'None' | 'Allow'

type WebsitePermissionField = 'website' | 'organizationJustification' | 'camera' | 'microphone'

type FieldErrors = Partial<Record<WebsitePermissionField, string[]>>

interface WebsitePermissionItem {
  website: string
  organizationJustification: string
  camera: PermissionOption
  microphone: PermissionOption
  errors?: FieldErrors
  id?: number | string
}

type WebsitePermissionRow = Omit<WebsitePermissionItem, 'errors'>

interface Policy {
  update?: boolean
}

interface WebsitePermissionsTooltips {
  website?: string
  organizationJustification?: string
  camera?: string
  microphone?: string
}

interface WebsitePermissionsProps {
  data: {
    websitePermissions?: WebsitePermissionItem[]
  }
  policy: Policy
  meta: {
    permissionOptions: string[]
  }
  tooltips?: WebsitePermissionsTooltips
}

interface DynamicListHeader {
  displayName: string
  key: WebsitePermissionField
  tooltip?: string
  tooltipPlacement?: string
  multiline?: boolean
  options?: { displayName: string; value: string }[]
}

const EMPTY_PERMISSION: WebsitePermissionRow = {
  website: '',
  organizationJustification: '',
  camera: 'None',
  microphone: 'None',
}

function normalizeWebsitePermissions(permissions?: WebsitePermissionItem[]): WebsitePermissionItem[] {
  return (permissions || []).map((permission) => ({
    website: permission.website || '',
    organizationJustification: permission.organizationJustification || '',
    camera: (permission.camera || 'None') as PermissionOption,
    microphone: (permission.microphone || 'None') as PermissionOption,
    ...(permission.errors && { errors: permission.errors }),
    ...(permission.id != null && { id: permission.id }),
  }))
}

function stripRowErrors(items: WebsitePermissionItem[]): WebsitePermissionRow[] {
  return items.map(({ errors: _errors, ...row }) => row)
}

function wrapInputWithErrors(input: React.ReactNode, messages?: string[]) {
  if (!Array.isArray(messages) || messages.length === 0) {
    return input
  }

  return (
    <div className="has-error">
      {input}
      <span className="help-block">{messages.join('. ')}</span>
    </div>
  )
}

function WebsitePermissions(props: WebsitePermissionsProps) {
  const { data, policy, meta: { permissionOptions }, tooltips = {} } = props
  const [items, setItems] = useState<WebsitePermissionItem[]>(
    () => normalizeWebsitePermissions(data.websitePermissions)
  )

  const onAddItem = useCallback(() => {
    setItems((previousItems) => [...previousItems, { ...EMPTY_PERMISSION }])
  }, [])

  const onRemoveItem = useCallback((_removedItem: WebsitePermissionRow, removedIndex: number) => {
    setItems((previousItems) => previousItems.filter((_item, index) => index !== removedIndex))
  }, [])

  const onChangeItem = useCallback((updatedItem: WebsitePermissionRow, updatedIndex: number) => {
    setItems((previousItems) => previousItems.map((item, index) => (
      index === updatedIndex ? { ...item, ...updatedItem } : item
    )))
  }, [])

  const headers = useMemo((): DynamicListHeader[] => {
    const options = permissionOptions.map((option) => ({ displayName: option, value: option }))

    return [
      {
        displayName: 'Website',
        key: 'website',
        tooltip: tooltips.website,
        tooltipPlacement: 'right',
      },
      {
        displayName: 'Organization Justification',
        key: 'organizationJustification',
        multiline: true,
        tooltip: tooltips.organizationJustification,
      },
      {
        displayName: 'Camera',
        key: 'camera',
        options,
        tooltip: tooltips.camera,
      },
      {
        displayName: 'Microphone',
        key: 'microphone',
        options,
        tooltip: tooltips.microphone,
      },
    ]
  }, [permissionOptions, tooltips])

  const renderInput = useCallback((
    input: React.ReactNode,
    header: DynamicListHeader,
    _row: WebsitePermissionRow,
    rowNum: number
  ) => {
    return wrapInputWithErrors(input, items[rowNum]?.errors?.[header.key])
  }, [items])

  return (
    <div className="website-permissions dynamic-list">
      {items.length === 0 ? (
        <input type="hidden" name={`${FIELD_PREFIX}[]`} value="" />
      ) : null}
      <DynamicList
        rows={stripRowErrors(items)}
        onRemoveItem={onRemoveItem}
        onAddItem={onAddItem}
        onChangeItem={onChangeItem}
        headers={headers}
        policy={policy}
        fieldName={FIELD_PREFIX}
        renderInput={renderInput}
      />
    </div>
  )
}

export default WebsitePermissions
